'use strict'
/**
 * COLA:la-pestana-de-ayer-se-rompe-tras-la-promocion · LA PESTAÑA DE AYER NO SE MUERE: COMPRUEBA LA VERSIÓN ANTES DE PEDIR UN TROZO.
 *
 * 🔴 EL HECHO, visto en el dispositivo por REALIDAD: una pestaña abierta desde antes de una promoción navega a una ruta que esa sesión no había visitado,
 * el trozo diferido de esa ruta ya no existe en el servidor (el build nuevo tiene otros hashes) y la app muere con «Algo no se cargó bien». «Reintentar»
 * **no** la salva —vuelve a montar el mismo import roto— y sólo «Recargar la app» la devuelve. Cinco promociones seguidas no lo reprodujeron;
 * **al sexto día sí** (orden del founder, 2026-10-02).
 *
 * LA REGLA, en una frase: antes de pedir cualquier trozo se compara la versión servida con la que esta pestaña cargó, y si el servidor ha cambiado de
 * versión la pestaña se recarga **una vez** en vez de pedir un fichero que ya no está.
 *
 * De dónde sale la versión: de `asset-manifest.json`, que el build genera con los nombres con hash de los ficheros. No hace falta inyectar nada en el
 * bundle ni un endpoint nuevo: la comparación es «el manifiesto que vi al cargar» contra «el manifiesto de ahora».
 *
 * FALLA ABIERTO, a propósito: si el manifiesto no se puede leer (desarrollo, red caída, un 404), **no se bloquea nada** — se intenta el import igual. Una
 * comprobación que impide cargar cuando ella misma falla es peor que no tenerla.
 *
 * Y LA SEGUNDA RED: si el import revienta igual con un error de trozo (un hash viejo que ya no existe, un `import()` dinámico que no resuelve), se recarga
 * una vez en vez de dejar la pantalla de error. La recarga lleva marca y ventana: nunca dos veces seguidas, para que un trozo que de verdad no existe no
 * convierta la app en un bucle de recargas.
 *
 * Pieza · quién la alimenta · quién la dispara: cargaDeTrozo · `asset-manifest.json` del build servido · cada `lazy()` de App.js (todos pasan por aquí) y
 * el boundary de la app, que usa la misma `recargaUnaVez` cuando el error es de trozo.
 */
const MANIFEST = '/asset-manifest.json'
const VENTANA_DE_RECARGA_MS = 60 * 1000 // una recarga por minuto como mucho: un trozo que no existe no puede volverse un bucle
const FRESCURA_MS = 5 * 1000            // la versión servida se relee en cada navegación, pero no en cada uno de los trozos que una navegación pide a la vez
const MARCA = 'pl_recarga_por_version'

let _versionDeEstaPestana = null
let _ultimaLectura = { cuando: 0, version: null }
let _avisado = false            // el aviso del manifiesto ilegible, una vez por sesión
let _ultimoFallo = 0            // y no se reintenta la lectura en cada trozo si acaba de fallar

/** el error es de trozo: el nombre que da webpack, el de los `import()` dinámicos de los navegadores, o un fallo de red al pedir un .js */
function esErrorDeTrozo(e) {
  if (!e) return false
  const nombre = String(e.name || '')
  const msg = String(e.message || e)
  return nombre === 'ChunkLoadError' || /Loading chunk|Loading CSS chunk|dynamically imported module|importing a module script failed|error loading dynamically imported/i.test(msg)
}

/** la versión servida (el `main.js` con hash del manifiesto), o null si no se puede leer. Nunca lanza. */
async function versionServida({ frescura = FRESCURA_MS } = {}) {
  const ahora = Date.now()
  if (_ultimaLectura.version && ahora - _ultimaLectura.cuando < frescura) return _ultimaLectura.version
  // si la lectura acaba de fallar, no se reintenta en cada trozo de la misma navegación (sin manifiesto no se bloquea nada igualmente)
  if (_ultimoFallo && ahora - _ultimoFallo < frescura) return null
  try {
    const res = await fetch(MANIFEST, { cache: 'no-store' })
    if (!res || !res.ok) { _ultimoFallo = ahora; return null }
    const json = await res.json()
    const v = (json && json.files && (json.files['main.js'] || json.files['main.css'])) || null
    if (v) _ultimaLectura = { cuando: ahora, version: v }; else _ultimoFallo = ahora
    return v
  } catch (e) {
    // COLA:catch-mudos-por-dano · se dice y se sigue: sin versión no se bloquea ningún trozo. Se dice UNA vez por sesión: con 59 trozos diferidos, un
    // manifiesto ilegible (desarrollo, un 404, la red caída) llenaría la consola de la misma línea — y en los tests multiplicaba el tiempo por cinco.
    if (!_avisado) { _avisado = true; if (typeof console !== 'undefined' && console.warn) console.warn('[cargaDeTrozo] la versión servida no se pudo leer; no se bloquea la carga', e && e.message) }
    _ultimoFallo = ahora
    return null
  }
}

/** la versión que esta pestaña cargó: se recuerda al arrancar. Sin ella no hay nada que comparar y no se bloquea nada. */
async function recordarVersion() {
  _versionDeEstaPestana = await versionServida({ frescura: 0 })
  return _versionDeEstaPestana
}

/**
 * Recarga la pestaña UNA vez por ventana, dejando dicho por qué. Devuelve true si recargó.
 * `recargar` tiene su valor por defecto y existe para que el guard pueda VERLO: jsdom no deja sustituir `window.location`, así que sin esta costura el
 * caso estaría midiendo el `reload` de jsdom en vez del de la app.
 */
function recargaUnaVez(motivo, recargar = () => window.location.reload()) {
  try {
    const previa = Number(window.sessionStorage.getItem(MARCA) || 0)
    if (previa && Date.now() - previa < VENTANA_DE_RECARGA_MS) return false
    window.sessionStorage.setItem(MARCA, String(Date.now()))
  } catch (_) {
    // sin sessionStorage (modo privado) se recarga igual: el riesgo de bucle es menor que el de dejar la app muerta
  }
  if (typeof console !== 'undefined' && console.warn) console.warn(`[cargaDeTrozo] recargo la pestaña: ${motivo}`)
  try { recargar() } catch (e) {
    if (typeof console !== 'undefined' && console.warn) console.warn('[cargaDeTrozo] la recarga no se pudo hacer', e && e.message)
    return false
  }
  return true
}

/**
 * Envuelve el `import()` de un trozo: comprueba la versión antes de pedirlo y recarga una vez si el servidor cambió o si el trozo no carga.
 * Se usa en TODOS los `lazy()` de la app; el guard lo comprueba.
 */
function cargaDeTrozo(importar, { recargar } = {}) {
  const _recarga = (motivo) => (recargar ? recargaUnaVez(motivo, recargar) : recargaUnaVez(motivo))
  return async () => {
    const ahora = await versionServida()
    if (_versionDeEstaPestana && ahora && ahora !== _versionDeEstaPestana) {
      // el servidor sirve otra versión: pedir este trozo sería pedir un fichero que ya no está
      if (_recarga('la versión servida cambió desde que esta pestaña cargó')) return new Promise(() => {}) // la página se va; no se resuelve
    }
    try {
      return await importar()
    } catch (e) {
      if (esErrorDeTrozo(e) && _recarga('el trozo no cargó (hash viejo tras una promoción)')) return new Promise(() => {})
      throw e
    }
  }
}

/** sólo para el guard: olvida la lectura cacheada de la versión servida (el tiempo no se puede adelantar en un test de jsdom sin relojes falsos) */
function _olvidarVersionServida() { _ultimaLectura = { cuando: 0, version: null }; _ultimoFallo = 0 }

module.exports = { cargaDeTrozo, recordarVersion, versionServida, recargaUnaVez, esErrorDeTrozo, MANIFEST, VENTANA_DE_RECARGA_MS, FRESCURA_MS, MARCA, _olvidarVersionServida }
